import React, { useEffect, useState } from 'react';
import {
  AvenyContextConsumer,
  ISingleRoutineMode,
} from '../../Features/AvenyContext';
import { rutinInfos } from '../../Features/Delsystem/_Common/Const/RutinInfo';
import { LoginStateContext } from '../../Features/Login/Contexts/LoginStateContext/LoginStateContext';
import ErrorBoundary from '../ErrorBoundary/ErrorBoundary';
import { getServerURL } from '../../Utils/getServerURL';
import { useLocation } from 'react-router';
import { useNavigate } from 'react-router';

interface IProps {}

interface IInnerProps extends IProps {
  isLoggedIn: boolean;
  refetchLoginState: () => void;
  didLoginViaSingleSignOn: boolean;
  setDidLoginViaSingleSignOn: (newvalue: boolean) => void;
  singleRoutineMode: ISingleRoutineMode;
  setParams: (params: ISingleSignOnParam[]) => void;
}

export interface ISingleSignOnParam {
  key: string;
  value: string;
}

const PARAMS_TO_SAVE: string[] = ['KodplanId'];

const login = (
  params: string,
  onError: (statusCode: number) => void,
  onSuccess: () => void
) => {
  const url = '/login' + params;

  fetch(getServerURL() + url, {
    method: 'GET',
    credentials: 'include',
  }).then(response => {
    if (!response.ok) {
      onError(response.status);
    } else {
      onSuccess();
    }
  });
};

export const findRoutineUrl = (url: string): string | null => {
  const regexp = /rutin\/([^/]*)/gi;
  const regexpResult = regexp.exec(url);
  if (regexpResult === null || regexpResult[1] == null) {
    return null;
  }

  return regexpResult[1].toLowerCase();
};

export const routineIdFromUrl = (url: string): number | null => {
  const routineUrl = findRoutineUrl(url);
  if (!routineUrl) {
    return null;
  }

  const rutin = Object.keys(rutinInfos).filter(rutinInfo => {
    if (
      Object.prototype.hasOwnProperty.call(
        !(rutinInfos as object),
        rutinInfo
      ) ||
      rutinInfos[rutinInfo] === undefined
    ) {
      return false;
    }
    return rutinInfos[rutinInfo].rutinURL.toLowerCase() === routineUrl;
  })[0];

  if (rutin === undefined) {
    return null;
  }

  return rutinInfos[rutin].rutinID;
};

const SingleSignOn: React.FC<React.PropsWithChildren<IInnerProps>> = props => {
  const { refetchLoginState, setDidLoginViaSingleSignOn } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  let paramsList: ISingleSignOnParam[] = [];
  params.forEach((value, key) => {
    paramsList.concat({ key, value });
  });
  const redirectUrl = location.pathname.replace('SingleSignOn/', '');

  const [error, setError] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (!props.isLoggedIn) {
      login(
        location.search,
        (statusCode: number) => {
          setError(statusCode);
          refetchLoginState();
        },
        () => {
          setDidLoginViaSingleSignOn(true);
          refetchLoginState();
          navigate(redirectUrl);
        }
      );
    }
  }, [
    location.search,
    navigate,
    props.isLoggedIn,
    refetchLoginState,
    setDidLoginViaSingleSignOn,
    redirectUrl,
  ]);

  useEffect(() => {
    if (props.isLoggedIn && props.didLoginViaSingleSignOn) {
      if (paramsList.some(param => PARAMS_TO_SAVE.includes(param.key))) {
        props.setParams(
          paramsList.filter(param => PARAMS_TO_SAVE.includes(param.key))
        );
      }

      navigate('/' + redirectUrl);
    }
  });

  if (error) {
    return <div>Kunde ej logga in ({error})</div>;
  }

  return null;
};

const EnhancedSingleSignOn: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  return (
    <ErrorBoundary name="SingleSignOn">
      <LoginStateContext.Consumer>
        {loginStateContext => (
          <AvenyContextConsumer>
            {context => {
              return (
                <SingleSignOn
                  isLoggedIn={loginStateContext.isLoggedIn}
                  refetchLoginState={loginStateContext.refetch}
                  didLoginViaSingleSignOn={context.didLoginViaSingleSignOn}
                  setDidLoginViaSingleSignOn={
                    context.setDidLoginViaSingleSignOn
                  }
                  singleRoutineMode={context.singleRoutineMode}
                  setParams={context.setSingleSignOnParams}
                  {...props}
                />
              );
            }}
          </AvenyContextConsumer>
        )}
      </LoginStateContext.Consumer>
    </ErrorBoundary>
  );
};

export default EnhancedSingleSignOn;
